import { useLocale } from '@mirai/locale';
import { ServiceBooking } from '@mirai/services';
import { Button, InputText, Modal, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';

const SCORES = [1, 2, 3, 4, 5];
const STEP = {
  COMMENT: 'comment',
  SCORE: 'score',
};

const ModalCesSurvey = ({ id, token, onClose = () => {}, ...others }) => {
  const { translate } = useLocale();

  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [score, setScore] = useState();
  const [step, setStep] = useState(STEP.SCORE);

  const handleDismiss = () => {
    if (id && step === STEP.SCORE) ServiceBooking.cesSurvey({ id, token }).catch(() => {});
    onClose();
  };

  const handleSubmitScore = async () => {
    setBusy(true);
    await ServiceBooking.cesSurvey({ id, score, token }).catch(() => {});
    setBusy(false);
    setStep(STEP.COMMENT);
  };

  const handleSubmitComment = async () => {
    setBusy(true);
    await ServiceBooking.cesSurvey({ id, comment, token }).catch(() => {});
    setBusy(false);
    onClose();
  };

  const renderScore = () => [
    <View key="content" className={style.cesSurveyContent}>
      <Text bold wide>
        {translate(L10N.LABEL_CES_SURVEY_QUESTION)}
      </Text>
      <View row className={style.cesSurveyScores}>
        {SCORES.map((value) => (
          <Button
            key={value}
            secondary={score !== value}
            squared
            onPress={() => setScore(value)}
            className={styles(style.cesSurveyScore, score === value && style.selected)}
            aria-label={translate(L10N.LABEL_CES_SURVEY_SCORE, { score: value })}
            testId={`booking-ces-survey-score-${value}`}
          >
            {value}
          </Button>
        ))}
      </View>
      <View row className={style.cesSurveyScale}>
        <Text action bold light>
          {translate(L10N.LABEL_CES_SURVEY_NOT_EASY)}
        </Text>
        <Text action bold light>
          {translate(L10N.LABEL_CES_SURVEY_VERY_EASY)}
        </Text>
      </View>
    </View>,
    <View key="footer" row className={style.cesSurveyFooter}>
      <Button busy={busy} disabled={!score} wide onPress={handleSubmitScore} testId="booking-ces-survey-next">
        {translate(L10N.ACTION_NEXT)}
      </Button>
    </View>,
  ];

  const renderComment = () => [
    <View key="content" className={style.cesSurveyContent}>
      <Text bold wide>
        {translate(L10N.LABEL_CES_SURVEY_COMMENT_QUESTION)}
      </Text>
      <InputText
        multiLine
        name="comment"
        label={translate(L10N.LABEL_CES_SURVEY_COMMENT)}
        value={comment}
        onChange={setComment}
        testId="booking-ces-survey-comment"
      />
    </View>,
    <View key="footer" row className={style.cesSurveyFooter}>
      <Button secondary wide onPress={onClose} testId="booking-ces-survey-skip">
        {translate(L10N.ACTION_SKIP)}
      </Button>
      <Button
        busy={busy}
        disabled={!comment.trim()}
        wide
        onPress={handleSubmitComment}
        testId="booking-ces-survey-send"
      >
        {translate(L10N.ACTION_SEND)}
      </Button>
    </View>,
  ];

  return (
    <Modal
      {...others}
      fit
      preventDefault={false}
      title={translate(L10N.LABEL_CES_SURVEY_TITLE)}
      onClose={handleDismiss}
      onOverflow={handleDismiss}
      className={style.modalCesSurvey}
      testId="booking-ces-survey"
    >
      {step === STEP.SCORE ? renderScore() : renderComment()}
    </Modal>
  );
};

ModalCesSurvey.displayName = 'Mirai:Core:Booking:ModalCesSurvey';

ModalCesSurvey.propTypes = {
  id: PropTypes.string,
  token: PropTypes.string,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
};

export { ModalCesSurvey };
